import React from 'react';
import { format, isToday } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { getStatusStyle, continuationsForDay } from './calendarUtils';
import { CardIndicators, CardStaffNotes } from './CalendarShiftCard';

const HEADERS = { participant: 'Participant', staff: 'Staff', sil: 'SIL House', centre: 'Centre Session' };

// Horizontal Gantt-like Timeline view across the visible week. Rows are server-paginated (same
// /roster/board payload as the Week view); only rows that have shifts are shown.
export function CalendarTimelineView({ days, rows, groupBy, onShiftClick, totalRows, hasMore, onLoadMore, loadingMore }) {
  const visible = (rows || []).filter(r => (r.shifts || []).length > 0);
  // Rows are sorted with-shifts-first server-side, so once a page contains an empty row every later row is empty too.
  const moreWithShifts = hasMore && visible.length === (rows || []).length;

  return (
    <div>
      <div className="flex sticky top-0 z-20 bg-white border-b">
        <div className="w-48 flex-shrink-0 p-2 border-r font-medium text-xs text-slate-600">
          {HEADERS[groupBy] || 'Staff'}
        </div>
        {days.map(day => (
          <div key={day.toISOString()} className={`flex-1 min-w-[100px] text-center py-1.5 border-r ${isToday(day) ? 'bg-blue-50' : ''}`}>
            <p className="text-[10px] text-muted-foreground">{format(day, 'EEE')}</p>
            <p className={`text-xs font-semibold ${isToday(day) ? 'text-blue-700' : ''}`}>{format(day, 'd')}</p>
          </div>
        ))}
      </div>
      {visible.map(row => (
        <div key={row.id} className="flex border-b hover:bg-slate-50/50" data-testid={`timeline-row-${row.id}`}>
          <div className="w-48 flex-shrink-0 p-2 border-r bg-white sticky left-0 z-10" data-testid={`row-label-${row.id}`}>
            <p className="text-xs font-medium truncate">{row.label}</p>
            <p className="text-[10px] text-muted-foreground">{row.shifts.length} shifts</p>
          </div>
          {days.map(day => {
            const dk = format(day, 'yyyy-MM-dd');
            const dayShifts = [...continuationsForDay(row.shifts, dk), ...row.shifts.filter(s => s.date === dk)];
            return (
              <div key={dk} className={`flex-1 min-w-[100px] p-0.5 border-r ${isToday(day) ? 'bg-blue-50/30' : ''}`}>
                {dayShifts.map(s => {
                  const style = getStatusStyle(s.status);
                  return (
                    <div key={s._continuation ? `cont-${s.id}` : s.id} className={`rounded px-1 py-0.5 mb-0.5 cursor-pointer hover:shadow ${style.bg} border ${s._continuation ? 'border-dashed opacity-80' : ''}`} onClick={() => onShiftClick(s)}
                      title={s._continuation ? `Continues from ${s.date} ${s.start_time}` : undefined} data-testid={s._continuation ? `timeline-cont-${s.id}` : undefined}>
                      <p className={`text-[9px] font-semibold ${style.text}`}>{s._continuation ? `↳ until ${s.end_time}` : `${s.start_time}–${s.end_time}`}</p>
                      {!s._continuation && <CardStaffNotes shift={s} />}
                      {!s._continuation && <CardIndicators shift={s} onGroup={onShiftClick} dense />}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ))}
      {visible.length === 0 && <div className="text-center py-12 text-muted-foreground text-sm" data-testid="calendar-no-rows">No shifts to display</div>}
      {moreWithShifts && (
        <div className="flex border-b bg-slate-50/60">
          <div className="w-full p-2 flex items-center justify-center">
            <button onClick={onLoadMore} disabled={loadingMore}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-60"
              data-testid="calendar-load-more-rows">
              {loadingMore && <Loader2 className="w-3 h-3 animate-spin" />}
              {loadingMore ? 'Loading…' : `Load more (${Math.max(0, totalRows - rows.length)} more)`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
